"use client";

import { Typography } from "@kyan/ui";
import Link from "next/link";

import { defaultUnderlying } from "#/config/markets";
import { routes } from "#/config/routes";
import { useThrottle } from "#/hooks/useThrottle";
import { useIndexPrices } from "#/store/app/indexPrices/indexPrices";
import { useSlippagePercentage } from "#/store/app/portfolio/settings/slippagePercentage";
import { useIsOptionHedged } from "#/store/options/tradePanel/isOptionHedged";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpsOrderbook } from "#/store/perps/page/perpsOrderbook";
import { calculateHedge } from "#/utils/calculateHedge";
import { createPerpInstrumentName } from "#/utils/instrument.utils";
import { formatValue } from "#/utils/valueFormatters";

import { useLastPerpTradesByInstrument } from "./useLastPerpTradesByInstrument";

export const useHedgeSummaryController = () => {
  const { option } = useOption();
  const { optionSize } = useOptionSize();
  const { setIsOptionHedged } = useIsOptionHedged();
  const { slippageFactor } = useSlippagePercentage();
  const { perpsOrderbook } = usePerpsOrderbook();

  const optionUnderlying = option?.underlying ?? defaultUnderlying;
  const hedgeInstrumentName = createPerpInstrumentName(optionUnderlying);

  const { lastTrade, isLoading } =
    useLastPerpTradesByInstrument(hedgeInstrumentName);
  const { indexPrices } = useIndexPrices();
  const indexPrice = useThrottle(indexPrices[optionUnderlying], 60_000);

  const hedgeDetails = indexPrice
    ? calculateHedge({
        option,
        optionSize,
        indexPrice,
        slippageFactor,
        perpsOrderbook,
      })
    : undefined;

  const priceDetails = [
    {
      label: "Limit Price",
      value: formatValue(hedgeDetails?.hedgeLimitPrice),
      unit: "USD",
      tooltip: (
        <div className="grid gap-2">
          <Typography size={12}>
            Limit price = index price + default slippage
          </Typography>
          <Link href={routes.portfolio.settings + "#slippage"}>
            <Typography
              size={12}
              className="text-brandBlue hover:text-primary transition-colors"
            >
              Manage slippage
            </Typography>
          </Link>
        </div>
      ),
    },
    {
      label: "Estimated execution price",
      value: formatValue(hedgeDetails?.hedgeEstimatedExecPrice),
      unit: "USD",
    },
  ];

  const handleRemoveHedge = () => {
    setIsOptionHedged(false);
  };

  return {
    isLoading,
    lastTrade,
    priceDetails,
    hedgeDetails,
    optionUnderlying,
    handleRemoveHedge,
  };
};
